import { createAction } from "redux-actions";
import * as AdminNavListActions from "./AdminNavListActions";
import { Thunk } from "./Thunk";
import { TreeBranch } from "../models/navigation";
import { Api } from "../services/Api";
import { LocalStorage } from "../services/Localstorage";
import Settings from "../settings";
import { Log } from "../utils/Log";

/** The interval timer keeping track of when to request navigation data */
let navRequestTimer: NodeJS.Timer | undefined;

/** The timer for how long the app to wait before reloading the navigation data */
let expectedRefetchTimer: NodeJS.Timer | undefined;

const prefix = "NAV_";

export class Constants {
    public static ON_INITIALIZED = prefix + "ON_INITIALIZED";
    public static ON_LOADING_CHANGE = prefix + "ON_LOADING_CHANGE";
    public static ON_NAVIGATION_FROM_STORAGE = prefix + "ON_NAVIGATION_FROM_STORAGE";
    public static ON_NAVIGATION_RECEIVED = prefix + "ON_NAVIGATION_RECEIVED";
    public static ON_NAVIGATION_REQUESTED = prefix + "ON_NAVIGATION_REQUESTED";
    public static UPDATED_CHILD_OF_PATH = prefix + "UPDATED_CHILD_OF_PATH";
    public static WEBSOCKET_CONNECTED = prefix + "WEBSOCKET_CONNECTED";
    public static WEBSOCKET_DISCONNECTED = prefix + "WEBSOCKET_DISCONNECTED";
}

export type NavigationReceivedPayload = TreeBranch[];

export const init = (): Thunk => async (dispatch, getState) => {
    let storedBranches = LocalStorage.getItem<TreeBranch[]>("state.nav.tree", true);

    if (!Array.isArray(storedBranches)) {
        LocalStorage.removeItem("state.nav.tree");
        storedBranches = undefined;
    }

    if (storedBranches !== undefined) {
        dispatch(navigationReceived(storedBranches));
        dispatch(navigationReceivedFromStorage());
    }

    // not connected to api
    if (!getState().conn.connected) {
        return;
    }

    // Start api call to retrieve navigation tree
    await dispatch(requestNavigation());
};

export const requestNavigation = (dispatchRequestedAction?: boolean): Thunk => async (dispatch, getState) => {
    if (dispatchRequestedAction === undefined || dispatchRequestedAction)
        dispatch(navigationRequested());

    const queryParams = { queryParams: { appId: Settings.AppID } };
    const navResult = await new Api().get<TreeBranch[]>(Api.endpoints.nav.public, queryParams);

    if (navResult.isSuccess && navResult.data) {
        await dispatch(navigationReceived(navResult.data));
        await dispatch(toggleInitialized(true));
    }
    else
        Log.error("Navigation request error", navResult.errors);
};

/**
 * Starts the timer of when the app expects navigation to be reloaded
 */
export const startExpectedRefetchTimer = (): Thunk => async (dispatch, getState) => {
    if (expectedRefetchTimer !== undefined)
        clearTimeout(expectedRefetchTimer);

    expectedRefetchTimer = setTimeout(() => {
        console.warn("We expected nav to be reloaded by socket. But it didn't happen, so let's force reload it.");
        dispatch(requestNavigation(false));
        dispatch(AdminNavListActions.loadData());
    }, 10000);
};

/**
 * Starts the timer to request navigation.
 */
export const startReqestNavigationTimer = (): Thunk => async (dispatch, getState) => {
    if (navRequestTimer !== undefined)
        clearTimeout(navRequestTimer);

    navRequestTimer = setTimeout(() => {
        dispatch(requestNavigation(false));
        dispatch(AdminNavListActions.loadData());
        if (expectedRefetchTimer !== undefined)
            clearTimeout(expectedRefetchTimer);
    }, 375);
};

export const navigationReceived = createAction<NavigationReceivedPayload, TreeBranch[]>(
    Constants.ON_NAVIGATION_RECEIVED, (branches) => (branches),
);

export const navigationReceivedFromStorage = createAction(
    Constants.ON_NAVIGATION_FROM_STORAGE,
);

export const navigationRequested = createAction(
    Constants.ON_NAVIGATION_REQUESTED,
);

export const toggleInitialized = createAction<boolean, boolean>(
    Constants.ON_INITIALIZED, (isInitialized) => (isInitialized),
);

export const toggleLoading = createAction<boolean, boolean>(
    Constants.ON_LOADING_CHANGE, (isLoading) => (isLoading),
);

export const updateChildOfPath = createAction<string | undefined, string | undefined>(
    Constants.UPDATED_CHILD_OF_PATH, (childOfPath) => (childOfPath),
);



// WEBPACK FOOTER //
// ./src/actions/NavigationActions.ts